// Fila offline da leitura pública (E5) — o celular do leiturista guarda a
// leitura em IndexedDB (foto inclusa, já reduzida) quando não há sinal, e a
// drena quando a internet volta: pela própria página (evento 'online' +
// intervalo) e, no Android, pelo Background Sync do leitura-sw.js mesmo com a
// página fechada.
//
// O envio é IDEMPOTENTE: cada leitura nasce com um envio_uuid e o backend tem
// índice único — página e service worker podem tentar ao mesmo tempo sem
// duplicar (resposta perdida na rede também não duplica).
//
// ⚠️ O service worker tem uma reimplementação enxuta destas funções em JS puro
// (public/leitura-sw.js) — se mudar nome de banco/store ou o formato da
// entrada, mudar lá também.

export type EntradaLeitura = {
  envio_uuid: string;
  token: string;
  valor: number;
  lido_em: string;              // horário do REGISTRO (não do envio)
  leiturista_nome: string;
  observacao: string | null;
  lat: number | null;
  lon: number | null;
  fotoBlob: Blob | null;
  fotoType: string | null;
  criado_em: string;
};

const DB_NOME = 'hd-leitura-outbox';
const STORE = 'pendentes';

function abrir(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const req = indexedDB.open(DB_NOME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'envio_uuid' });
      }
    };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

function tx<T>(modo: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return abrir().then((db) => new Promise<T>((res, rej) => {
    const t = db.transaction(STORE, modo);
    const r = fn(t.objectStore(STORE));
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
    t.oncomplete = () => db.close();
  }));
}

export const salvar = (e: EntradaLeitura) => tx('readwrite', (s) => s.put(e));
export const listar = () => tx<EntradaLeitura[]>('readonly', (s) => s.getAll() as IDBRequest<EntradaLeitura[]>);
export const remover = (uuid: string) => tx('readwrite', (s) => s.delete(uuid));
export const contar = () => tx<number>('readonly', (s) => s.count());

// POST JSON com distinção entre falha de REDE (fica na fila) e REJEIÇÃO 4xx
// (sai da fila — token inválido não vira veneno que trava as demais).
async function postJson(url: string, body: unknown): Promise<any> {
  const r = await fetch(url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const e: any = new Error(`HTTP ${r.status}`);
    e.definitiva = r.status >= 400 && r.status < 500;
    throw e;
  }
  const j = await r.json().catch(() => ({}));
  return j?.body ? JSON.parse(j.body) : j;
}

// Envia UMA entrada (foto → presign + PUT; depois o registro). Lança em falha.
export async function enviarEntrada(apiUrl: string, e: EntradaLeitura): Promise<void> {
  let foto_s3: string | null = null;
  if (e.fotoBlob) {
    const d1 = await postJson(`${apiUrl}/publico/proposta/${e.token}`,
      { action: 'estacao_foto_url', content_type: e.fotoType || 'image/jpeg' });
    const r2 = await fetch(d1.upload_url, {
      method: 'PUT', body: e.fotoBlob,
      headers: { 'Content-Type': e.fotoType || 'image/jpeg' },
    });
    if (!r2.ok) throw new Error(`upload ${r2.status}`);
    foto_s3 = d1.s3_key;
  }
  await postJson(`${apiUrl}/publico/proposta/${e.token}`, {
    action: 'estacao_leitura', envio_uuid: e.envio_uuid, valor: e.valor,
    lido_em: e.lido_em, leiturista_nome: e.leiturista_nome, foto_s3,
    lat: e.lat, lon: e.lon, observacao: e.observacao,
  });
}

// Drena a fila em ordem; para na primeira falha de rede (sem sinal de novo).
// Rejeição definitiva (4xx) remove a entrada pra não travar as seguintes.
export async function drenar(apiUrl: string): Promise<number> {
  const fila = await listar();
  fila.sort((a, b) => a.criado_em.localeCompare(b.criado_em));
  for (const e of fila) {
    try {
      await enviarEntrada(apiUrl, e);
      await remover(e.envio_uuid);
    } catch (err: any) {
      if (err?.definitiva) { await remover(e.envio_uuid); continue; }
      break;
    }
  }
  return contar();
}
